import CustomTooltip from './CustomTooltip';
import { TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import dayjs from 'dayjs';
import React from 'react';
import { Control, Controller } from 'react-hook-form';

interface FormDateFilterProps {
    id?: string;
    name: string;
    control: Control<any>;
    label?: string;
    views?: Array<'day' | 'month' | 'year'>;
    format?: string;
    disabled?: boolean;
    shouldDisableDate?: (date: dayjs.Dayjs) => boolean;
    getDisabledDateMessage?: (date: dayjs.Dayjs) => string | undefined;
    defaultCalendarMonth?: dayjs.Dayjs;
}

const TooltipDay = (
    props: PickersDayProps<dayjs.Dayjs> & {
        getDisabledDateMessage?: (date: dayjs.Dayjs) => string | undefined;
    },
) => {
    const { getDisabledDateMessage, ...dayProps } = props;
    const message =
        dayProps.disabled && getDisabledDateMessage
            ? getDisabledDateMessage(dayProps.day)
            : undefined;

    if (!message) return <PickersDay {...dayProps} />;

    return (
        <CustomTooltip title={message} arrow placement='top'>
            <span>
                <PickersDay {...dayProps} />
            </span>
        </CustomTooltip>
    );
};

const FormDateFilter = ({
    id,
    name,
    control,
    label,
    views,
    format,
    shouldDisableDate,
    getDisabledDateMessage,
    defaultCalendarMonth,
    ...props
}: FormDateFilterProps) => {
    const DayWithTooltip = (dayProps: PickersDayProps<dayjs.Dayjs>) => (
        <TooltipDay
            {...dayProps}
            getDisabledDateMessage={getDisabledDateMessage}
        />
    );

    return (
        <Controller
            name={name}
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        {...props}
                        views={views}
                        format={format ? format : 'DD/MM/YYYY'}
                        label={label}
                        value={value ? dayjs(value) : null}
                        onChange={(date) => {
                            const formattedDate = date
                                ? dayjs(date).format('YYYY-MM-DD')
                                : '';
                            onChange(formattedDate);
                        }}
                        slots={{
                            textField: TextField,
                            day: DayWithTooltip,
                        }}
                        defaultCalendarMonth={defaultCalendarMonth}
                        shouldDisableDate={shouldDisableDate}
                        slotProps={{
                            openPickerButton: { tabIndex: -1 },
                            textField: {
                                id,
                                fullWidth: true,
                                error: !!error,
                                helperText: error?.message,
                                size: 'small',
                                sx: {
                                    '& .MuiInputBase-input': {
                                        fontSize: '14px',
                                    },
                                    '& .MuiInputLabel-root': {
                                        fontSize: '14px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        fontSize: '16px',
                                    },
                                },
                            },
                        }}
                    />
                </LocalizationProvider>
            )}
        />
    );
};

export default FormDateFilter;
